import React, { useState, useEffect } from 'react';
import api from '../services/api';

const RenameCultivar = ({ crop }) => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [cultivars, setCultivars] = useState([]);
  const [selectedCultivar, setSelectedCultivar] = useState('');
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [cultivarsLoading, setCultivarsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTables();
  }, [crop]);

  useEffect(() => {
    if (selectedTable) {
      fetchCultivars();
    } else {
      setCultivars([]);
      setSelectedCultivar('');
    }
  }, [selectedTable]);

  const fetchTables = async () => {
    try {
      let url = '/api/data/tables';
      if (crop) url += `?crop=${encodeURIComponent(crop)}`;
      const response = await api.get(url);
      setTables(response.data.tables);
    } catch (err) {
      setError('Failed to fetch tables: ' + (err.response?.data?.detail || err.message));
    }
  };

  const fetchCultivars = async () => {
    try {
      setCultivarsLoading(true);
      setError('');
      const response = await api.get(`/api/data/cultivars/${selectedTable}`);
      setCultivars(response.data.cultivars);
    } catch (err) {
      setError('Failed to fetch cultivars: ' + (err.response?.data?.detail || err.message));
      setCultivars([]);
    } finally {
      setCultivarsLoading(false);
    }
  };

  const handleRename = async (e) => {
    e.preventDefault();
    
    if (!selectedTable || !selectedCultivar || !newName.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (selectedCultivar === newName.trim()) {
      setError('New name is the same as old name');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const response = await api.post('/api/data/rename-cultivar', null, {
        params: {
          table_name: selectedTable,
          old_name: selectedCultivar,
          new_name: newName.trim()
        }
      });

      setMessage(response.data.message + ` (${response.data.rows_updated} rows updated)`);
      setNewName('');
      
      // Refresh cultivar list
      await fetchCultivars();
      setSelectedCultivar('');
      
    } catch (err) {
      setError('Failed to rename cultivar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.heading}>🏷️ Rename Cultivar</h2>
        
        <div style={styles.infoBox}>
          <p style={styles.infoText}>
            Rename a cultivar (genotype/sample) across all markers in a JSONB table. 
            This will update the cultivar name in every row of the selected table.
          </p>
        </div>

        {error && (
          <div style={styles.errorBox}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {message && (
          <div style={styles.successBox}>
            <strong>Success:</strong> {message}
          </div>
        )}

        <form onSubmit={handleRename}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Select Table:
              <select
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                style={styles.select}
                required
              >
                <option value="">-- Select a table --</option>
                {tables.map((table) => (
                  <option key={table} value={table}>
                    {table}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {selectedTable && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Select Cultivar to Rename:
                  {cultivarsLoading ? (
                    <div style={styles.loading}>Loading cultivars...</div>
                  ) : (
                    <select
                      value={selectedCultivar}
                      onChange={(e) => setSelectedCultivar(e.target.value)}
                      style={styles.select}
                      required
                      disabled={cultivars.length === 0}
                    >
                      <option value="">-- Select a cultivar --</option>
                      {cultivars.map((cultivar) => (
                        <option key={cultivar} value={cultivar}>
                          {cultivar}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
                {cultivars.length > 0 && (
                  <div style={styles.helperText}>
                    {cultivars.length} cultivars found in this table
                  </div>
                )}
              </div>

              {selectedCultivar && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    New Cultivar Name:
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      style={styles.input}
                      placeholder="Enter new name"
                      required
                    />
                  </label>
                  <div style={styles.helperText}>
                    Renaming: <strong>{selectedCultivar}</strong> → <strong>{newName || '...'}</strong>
                  </div>
                </div>
              )}
            </>
          )}

          <button
            type="submit"
            style={{
              ...styles.button,
              opacity: loading || !selectedTable || !selectedCultivar || !newName.trim() ? 0.6 : 1,
              cursor: loading || !selectedTable || !selectedCultivar || !newName.trim() ? 'not-allowed' : 'pointer'
            }}
            disabled={loading || !selectedTable || !selectedCultivar || !newName.trim()}
          >
            {loading ? 'Renaming...' : 'Rename Cultivar'}
          </button>
        </form>

        <div style={styles.warningBox}>
          <strong>⚠️ Warning:</strong> This action cannot be undone. The cultivar name will be 
          changed in all markers of the selected table. Make sure the new name is correct before proceeding.
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '30px',
  },
  heading: {
    color: '#025c4f',
    marginBottom: '20px',
    fontSize: '28px',
  },
  infoBox: {
    backgroundColor: '#e8f4f8',
    border: '1px solid #b8dce8',
    borderRadius: '6px',
    padding: '15px',
    marginBottom: '25px',
  },
  infoText: {
    margin: 0,
    color: '#0c5460',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  errorBox: {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '6px',
    padding: '15px',
    marginBottom: '20px',
    color: '#721c24',
  },
  successBox: {
    backgroundColor: '#d4edda',
    border: '1px solid #c3e6cb',
    borderRadius: '6px',
    padding: '15px',
    marginBottom: '20px',
    color: '#155724',
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '6px',
    padding: '15px',
    marginTop: '25px',
    color: '#856404',
    fontSize: '14px',
  },
  formGroup: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '8px',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    marginTop: '8px',
    backgroundColor: 'white',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    marginTop: '8px',
  },
  button: {
    backgroundColor: '#025c4f',
    color: 'white',
    border: 'none',
    padding: '14px 30px',
    fontSize: '16px',
    borderRadius: '6px',
    fontWeight: 'bold',
    width: '100%',
    marginTop: '10px',
    transition: 'background-color 0.3s',
  },
  loading: {
    padding: '12px',
    color: '#666',
    fontStyle: 'italic',
  },
  helperText: {
    fontSize: '13px',
    color: '#666',
    marginTop: '8px',
    fontStyle: 'italic',
  },
};

export default RenameCultivar;
